import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { ArrowLeft, Sparkles, BookOpen, Loader2, RefreshCw, TrendingUp, Heart, Target, Download, Mail } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { toast } from "sonner";
import {
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, Tooltip, Cell
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResultsPage() {
  const { sessionId } = useParams();
  const { t, i18n } = useTranslation();
  const [results, setResults] = useState(null);
  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);

  useEffect(() => {
    fetchResults();
    fetchExercises();
  }, [sessionId]);

  const fetchResults = async () => {
    try {
      const response = await axios.get(`${API}/diagnostic/${sessionId}`);
      setResults(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching results:", error);
      setLoading(false);
    }
  };

  const fetchExercises = async () => {
    try {
      const response = await axios.get(`${API}/exercises?lang=${i18n.language}`);
      setExercises(response.data);
    } catch (error) {
      console.error("Error fetching exercises:", error);
    }
  };

  const handleDownloadPdf = () => {
    window.open(`${API}/diagnostic/${sessionId}/pdf?lang=${i18n.language}`, '_blank');
  };

  const handleSendEmail = async () => {
    if (!email) return;
    setSendingEmail(true);
    try {
      await axios.post(`${API}/diagnostic/send-email`, {
        session_id: sessionId,
        recipient_email: email,
        language: i18n.language
      });
      toast.success(t('results.emailSent'));
      setEmailModalOpen(false);
      setEmail("");
    } catch (error) {
      toast.error(t('results.emailError'));
    } finally {
      setSendingEmail(false);
    }
  };

  const getRecommendedExercises = () => {
    if (!results || !exercises.length) return [];
    return exercises.filter(ex => results.recommended_exercises.includes(ex.id));
  };

  const getChartData = () => {
    if (!results?.scores?.categories) return [];
    return Object.entries(results.scores.categories).map(([key, value]) => ({
      category: t(`categories.${key}`),
      score: value,
      fullMark: 100
    }));
  };

  const getBarChartData = () => {
    if (!results?.scores?.categories) return [];
    return Object.entries(results.scores.categories)
      .map(([key, value]) => ({
        name: t(`categories.${key}`).split(' ')[0],
        fullName: t(`categories.${key}`),
        score: value
      }))
      .sort((a, b) => b.score - a.score);
  };

  const getScoreColor = (score) => {
    if (score >= 80) return "#A8C3A0";
    if (score >= 60) return "#2F4F4F";
    if (score >= 40) return "#E2725B";
    return "#DC2626";
  };

  const getScoreLevel = (score) => {
    if (score >= 80) return { label: t('results.excellent'), color: "text-[#A8C3A0]" };
    if (score >= 60) return { label: t('results.good'), color: "text-[#2F4F4F]" };
    if (score >= 40) return { label: t('results.toDevelop'), color: "text-[#E2725B]" };
    return { label: t('results.priority'), color: "text-red-500" };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-[#2F4F4F] animate-spin mx-auto mb-4" />
          <p className="text-[#5C5C5C]">{t('results.analyzing')}</p>
        </div>
      </div>
    );
  }

  if (!results) {
    return (
      <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center">
        <div className="text-center">
          <p className="text-[#5C5C5C] mb-4">{t('results.notFound')}</p>
          <Link to="/diagnostic">
            <Button className="rounded-full bg-[#2F4F4F] text-white">{t('results.redoDiagnostic')}</Button>
          </Link>
        </div>
      </div>
    );
  }

  const overallScore = results.scores?.overall || 0;
  const scoreLevel = getScoreLevel(overallScore);
  const chartData = getChartData();
  const barData = getBarChartData();
  const recommendedExercises = getRecommendedExercises();

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <header className="fixed top-0 left-0 right-0 z-50 glass-header border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-[#2F4F4F] hover:opacity-80" data-testid="back-home-link">
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">{t('nav.home')}</span>
          </Link>
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#2F4F4F]" />
            <span className="font-heading text-lg text-[#2F4F4F]">{t('results.title')}</span>
          </div>
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <Link to="/diagnostic" data-testid="redo-diagnostic-btn">
              <Button variant="outline" className="rounded-full border-gray-200">
                <RefreshCw className="w-4 h-4 mr-2" />{t('results.redo')}
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="pt-28 pb-24 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Action Buttons */}
          <div className="flex justify-end gap-3 mb-6">
            <Button 
              variant="outline" 
              onClick={handleDownloadPdf}
              className="rounded-full border-gray-200"
              data-testid="download-pdf-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              {t('results.downloadPdf')}
            </Button>
            <Button 
              onClick={() => setEmailModalOpen(true)}
              className="rounded-full bg-[#E2725B] hover:bg-[#E2725B]/90 text-white"
              data-testid="send-email-btn"
            >
              <Mail className="w-4 h-4 mr-2" />
              {t('results.sendEmail')}
            </Button>
          </div>

          {/* Overall Score */}
          <div className="mb-12 animate-fade-in">
            <Card className="bg-white border-gray-100 shadow-lg">
              <CardContent className="p-8">
                <div className="grid md:grid-cols-2 gap-8 items-center">
                  <div>
                    <h1 className="font-heading text-3xl font-semibold text-[#2D2D2D] mb-4" data-testid="results-title">
                      {t('results.profileTitle')}
                    </h1>
                    <p className="text-[#5C5C5C] mb-6">{t('results.profileDescription')}</p>
                    <div className="flex items-center gap-6">
                      <div className="relative w-32 h-32">
                        <svg className="w-full h-full transform -rotate-90">
                          <circle cx="64" cy="64" r="56" fill="none" stroke="#E5E5E5" strokeWidth="12" />
                          <circle cx="64" cy="64" r="56" fill="none" stroke={getScoreColor(overallScore)} strokeWidth="12" strokeLinecap="round" strokeDasharray={`${(overallScore / 100) * 352} 352`} />
                        </svg>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-3xl font-bold text-[#2D2D2D]" data-testid="overall-score">{Math.round(overallScore)}%</span>
                        </div>
                      </div>
                      <div>
                        <p className={`text-xl font-semibold ${scoreLevel.color}`}>{scoreLevel.label}</p>
                        <p className="text-sm text-[#8A8A8A]">{t('results.overallScore')}</p>
                      </div>
                    </div>
                  </div>
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <RadarChart data={chartData}>
                        <PolarGrid stroke="#E5E5E5" />
                        <PolarAngleAxis dataKey="category" tick={{ fill: '#5C5C5C', fontSize: 10 }} />
                        <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fill: '#8A8A8A', fontSize: 10 }} />
                        <Radar name="Score" dataKey="score" stroke="#2F4F4F" fill="#2F4F4F" fillOpacity={0.3} />
                      </RadarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Bar Chart & Legend */}
          <div className="grid lg:grid-cols-3 gap-8 mb-12">
            <div className="lg:col-span-2">
              <Card className="bg-white border-gray-100 shadow-lg h-full">
                <CardHeader>
                  <CardTitle className="font-heading text-xl flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-[#2F4F4F]" />{t('results.categoryDetail')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={barData} layout="vertical" margin={{ left: 20 }}>
                        <XAxis type="number" domain={[0, 100]} />
                        <YAxis type="category" dataKey="name" width={80} tick={{ fontSize: 11, fill: '#5C5C5C' }} />
                        <Tooltip formatter={(value) => [`${value}%`, 'Score']} labelFormatter={(label, payload) => payload?.[0]?.payload?.fullName || label} />
                        <Bar dataKey="score" radius={[0, 8, 8, 0]}>
                          {barData.map((entry, index) => (<Cell key={`cell-${index}`} fill={getScoreColor(entry.score)} />))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>
            <Card className="bg-white border-gray-100 shadow-lg">
              <CardHeader><CardTitle className="font-heading text-xl flex items-center gap-2"><Target className="w-5 h-5 text-[#2F4F4F]" />{t('results.legend')}</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <LegendItem color="#A8C3A0" label={t('results.excellent')} range="80-100%" />
                <LegendItem color="#2F4F4F" label={t('results.good')} range="60-79%" />
                <LegendItem color="#E2725B" label={t('results.toDevelop')} range="40-59%" />
                <LegendItem color="#DC2626" label={t('results.priority')} range="0-39%" />
              </CardContent>
            </Card>
          </div>

          {/* AI Analysis */}
          <div className="mb-12">
            <Card className="bg-white border-gray-100 shadow-lg">
              <CardHeader><CardTitle className="font-heading text-xl flex items-center gap-2"><Sparkles className="w-5 h-5 text-[#E2725B]" />{t('results.aiAnalysis')}</CardTitle></CardHeader>
              <CardContent>
                <div className="prose prose-slate max-w-none text-[#2D2D2D] leading-relaxed" data-testid="ai-analysis">
                  {results.ai_analysis.split('\n').map((p, i) => <p key={i} className="mb-4 whitespace-pre-wrap">{p}</p>)}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Recommended Exercises */}
          {recommendedExercises.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-heading text-2xl font-semibold text-[#2D2D2D] flex items-center gap-2">
                  <Heart className="w-6 h-6 text-[#E2725B]" />{t('results.recommendedExercises')}
                </h2>
                <Link to="/exercises" data-testid="view-all-exercises">
                  <Button variant="outline" className="rounded-full border-gray-200">{t('results.viewAllExercises')}<BookOpen className="w-4 h-4 ml-2" /></Button>
                </Link>
              </div>
              <div className="grid md:grid-cols-2 gap-6">
                {recommendedExercises.map((ex) => <ExerciseCard key={ex.id} exercise={ex} t={t} />)}
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Email Modal */}
      <Dialog open={emailModalOpen} onOpenChange={setEmailModalOpen}>
        <DialogContent className="sm:max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">{t('results.sendEmail')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Input
              type="email"
              placeholder={t('results.enterEmail')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="h-12 rounded-xl"
              data-testid="email-input"
            />
            <Button 
              onClick={handleSendEmail}
              disabled={sendingEmail || !email}
              className="w-full rounded-full bg-[#2F4F4F] hover:bg-[#2F4F4F]/90 text-white"
              data-testid="confirm-send-email"
            >
              {sendingEmail ? <Loader2 className="w-5 h-5 animate-spin" /> : t('results.sendEmail')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function LegendItem({ color, label, range }) {
  return (<div className="flex items-center gap-3"><div className="w-4 h-4 rounded-full" style={{ backgroundColor: color }} /><div><p className="font-medium text-[#2D2D2D]">{label}</p><p className="text-sm text-[#8A8A8A]">{range}</p></div></div>);
}

function ExerciseCard({ exercise, t }) {
  const [expanded, setExpanded] = useState(false);
  const getDifficultyColor = (d) => d === 'Facile' || d === 'Easy' ? 'bg-[#A8C3A0] text-white' : d === 'Moyen' || d === 'Medium' ? 'bg-[#E2725B] text-white' : 'bg-[#2F4F4F] text-white';
  return (
    <Card className="bg-white border-gray-100 shadow-lg hover:shadow-xl cursor-pointer" onClick={() => setExpanded(!expanded)} data-testid={`exercise-card-${exercise.id}`}>
      <CardContent className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="font-heading text-lg font-semibold text-[#2D2D2D] mb-2">{exercise.title}</h3>
            <div className="flex gap-2">
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${getDifficultyColor(exercise.difficulty)}`}>{exercise.difficulty}</span>
              <span className="px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-[#5C5C5C]">{exercise.duration}</span>
            </div>
          </div>
        </div>
        <p className="text-[#5C5C5C] text-sm mb-4">{exercise.description}</p>
        {expanded && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            <h4 className="font-medium text-[#2D2D2D] mb-3">{t('exercises.steps')}</h4>
            <ol className="space-y-2">
              {exercise.steps.map((step, i) => (<li key={i} className="flex items-start gap-3"><span className="w-6 h-6 rounded-full bg-[#A8C3A0]/20 text-[#2F4F4F] text-sm font-medium flex items-center justify-center flex-shrink-0">{i + 1}</span><span className="text-sm text-[#5C5C5C]">{step}</span></li>))}
            </ol>
          </div>
        )}
        <p className="text-xs text-[#8A8A8A] mt-4">{expanded ? t('exercises.clickToCollapse') : t('exercises.clickToExpand')}</p>
      </CardContent>
    </Card>
  );
}
